/**
 * useTranscriptWsStore — reactive live-transcript segments per hearing.
 *
 * TranscriptPanel + AINotesPanel read from this store. Segments arrive
 * via the shared platform /ws on the ``adaptcx:ws:hearing:{id}`` channel
 * as ``transcript.segment`` events (bot-audiohook publishes on final);
 * ``useDarsEvents::handleMessage`` calls ``appendSegment()`` on match.
 * Legacy per-hearing ``/ws/transcript`` bridge was removed in dars/v0.9.43.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface TranscriptSegment {
  id?: string
  hearing_id: string
  timestamp: string
  text: string
  participant_name?: string | null
  participant_id?: string | null
  speaker_role?: string | null
  is_final?: boolean
}

/** Cap the retained segments per hearing so long hearings don't OOM the tab. */
const MAX_SEGMENTS_PER_HEARING = 500

export const useTranscriptWsStore = defineStore('wsTranscript', () => {
  const segmentsByHearingId = ref<Record<string, TranscriptSegment[]>>({})

  function appendSegment(segment: TranscriptSegment) {
    if (!segment.hearing_id) return
    const existing = segmentsByHearingId.value[segment.hearing_id] || []
    const next = [...existing, segment]
    // Trim old segments if we exceeded the cap (keep the most recent).
    const trimmed =
      next.length > MAX_SEGMENTS_PER_HEARING
        ? next.slice(next.length - MAX_SEGMENTS_PER_HEARING)
        : next
    segmentsByHearingId.value = {
      ...segmentsByHearingId.value,
      [segment.hearing_id]: trimmed,
    }
  }

  function replaceSegments(hearingId: string, segments: TranscriptSegment[]) {
    segmentsByHearingId.value = {
      ...segmentsByHearingId.value,
      [hearingId]: segments.slice(-MAX_SEGMENTS_PER_HEARING),
    }
  }

  function segmentsFor(hearingId: string): TranscriptSegment[] {
    return segmentsByHearingId.value[hearingId] || []
  }

  function clearHearing(hearingId: string) {
    const next = { ...segmentsByHearingId.value }
    delete next[hearingId]
    segmentsByHearingId.value = next
  }

  function reset() {
    segmentsByHearingId.value = {}
  }

  return {
    segmentsByHearingId,
    appendSegment,
    replaceSegments,
    segmentsFor,
    clearHearing,
    reset,
  }
})
